import { API_METHODS } from '@compass/client/constants/api-methods';
import { useStore } from '@store/index';
import { Download } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounce } from '@/hooks/useDebounce';
import { useAPIPermission, usePermissionMap } from '@/hooks/usePermissions';
import { callConsoleService } from '@/services/callConsole.service';
import { downloadBlob } from '@/utils/download';
import { buildRoutePath } from '@/utils/routes';

import { EventLog } from './components/EventLog';
import { LogView } from './components/LogView';
import { PromptSheet } from './components/PromptSheet';
import { SidePanels } from './components/SidePanels';
import { SyncedTranscript } from './components/SyncedTranscript';
import {
  useCallConsole,
  useRecordingUrl,
  useSessionLog,
  useUtteranceAudio,
} from './hooks/useCallConsole';
import { type ConsoleEvent, findSystemPrompt } from './utils/richTranscript';
import { sliceUtterance } from './utils/wavClip';

type ConsoleTab = 'events' | 'transcript' | 'log';

/**
 * Internal Call Console (JNJ-194): the AI agent's rich transcript, prompts, session log
 * and audio for one call. Reachable only with the global call_console permission.
 */
export const CallConsolePage: React.FC = () => {
  const { caseId = '', callId = '', orgId = '', progId = '' } = useParams();
  const organizationId = useStore(state => state.selectedOrganizationId) ?? '';
  const programId = useStore(state => state.selectedProgramId) ?? '';
  const ctx = useMemo(
    () => ({ organizationId, programId, caseRecordId: caseId, callId }),
    [organizationId, programId, caseId, callId],
  );

  const { isLoading: permissionsLoading } = usePermissionMap();
  const { hasPermission } = useAPIPermission(API_METHODS.AssetService.ListCallConsoleAssets);
  const ready = hasPermission && !!organizationId && !!programId && !!caseId && !!callId;

  const consoleData = useCallConsole(ctx, ready);
  const { parsed, richAsset, logAsset, utterancesAsset, recordingAsset } = consoleData;

  const [tab, setTab] = useState<ConsoleTab>('events');
  const [query, setQuery] = useState('');
  // Each response.create carries the full prompt, so search is debounced over large files.
  const searchQuery = useDebounce(query, 200);
  const [promptTurn, setPromptTurn] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [wantClips, setWantClips] = useState(false);
  const [pendingClip, setPendingClip] = useState<ConsoleEvent | null>(null);

  const recordingRef = useRef<HTMLAudioElement | null>(null);
  const clipRef = useRef<HTMLAudioElement | null>(null);
  const clipUrlRef = useRef<string | null>(null);

  const recording = useRecordingUrl(ctx, recordingAsset, ready);
  const log = useSessionLog(ctx, logAsset, ready && tab === 'log');
  const utterances = useUtteranceAudio(ctx, utterancesAsset, ready && wantClips);

  const seek = useCallback((seconds: number) => {
    const audio = recordingRef.current;
    if (!audio) return;
    audio.currentTime = seconds;
    void audio.play().catch(() => undefined);
  }, []);

  const playClip = useCallback(
    (event: ConsoleEvent) => {
      if (!event.userClip) return;
      if (!utterances.buffer || !utterances.chunk) {
        setWantClips(true);
        setPendingClip(event);
        return;
      }
      const wav = sliceUtterance(
        utterances.buffer,
        utterances.chunk,
        event.userClip.offset,
        event.userClip.length,
      );
      if (!wav || !clipRef.current) {
        toast.error('This utterance is not in the audio file');
        return;
      }
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
      clipUrlRef.current = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
      recordingRef.current?.pause();
      clipRef.current.src = clipUrlRef.current;
      void clipRef.current.play().catch(() => undefined);
    },
    [utterances.buffer, utterances.chunk],
  );

  // Play the clip that triggered the utterance download once it arrives.
  useEffect(() => {
    if (pendingClip && utterances.buffer) {
      setPendingClip(null);
      playClip(pendingClip);
    }
  }, [pendingClip, utterances.buffer, playClip]);

  useEffect(
    () => () => {
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    },
    [],
  );

  const prompt = useMemo(
    () => (promptTurn && parsed ? findSystemPrompt(parsed.events, promptTurn) : null),
    [promptTurn, parsed],
  );

  const download = async (assetId: string | undefined, filename: string) => {
    if (!assetId) return;
    try {
      const bytes = await callConsoleService.downloadBytes(ctx, assetId);
      downloadBlob(new Blob([bytes]), filename);
    } catch {
      toast.error('Download failed');
    }
  };

  const casePath = buildRoutePath(`/cases/${caseId}`, orgId, progId);

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item to={buildRoutePath('/cases', orgId, progId)}>Cases</Breadcrumb.Item>
        <Breadcrumb.Item to={`${casePath}?callId=${callId}`}>Case</Breadcrumb.Item>
        <Breadcrumb.Item>Console</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (permissionsLoading) return breadcrumb;

  if (!hasPermission) {
    return (
      <>
        {breadcrumb}
        <p className="p-6 text-sm text-muted-foreground">
          You don&apos;t have access to the Call Console.
        </p>
      </>
    );
  }

  const body = (() => {
    if (consoleData.isLoading)
      return <p className="p-6 text-sm text-muted-foreground">Loading call files…</p>;
    if (consoleData.error)
      return <p className="p-6 text-sm text-destructive">Could not load the call files.</p>;
    if (!richAsset || !parsed) {
      return (
        <p className="p-6 text-sm text-muted-foreground">
          The Console is available once the call has ended and its files are uploaded. The live
          transcript is on the case page.
        </p>
      );
    }
    return (
      <div className="flex min-h-0 flex-1 gap-4">
        <Tabs
          value={tab}
          onValueChange={v => setTab(v as ConsoleTab)}
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
          <TabsList className="self-start">
            <TabsTrigger value="events">Events ({parsed.events.length})</TabsTrigger>
            <TabsTrigger value="transcript">Transcript</TabsTrigger>
            <TabsTrigger value="log">Log</TabsTrigger>
          </TabsList>
          <TabsContent
            value="events"
            className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
          >
            <EventLog
              events={parsed.events}
              query={searchQuery}
              onPlayUserClip={playClip}
              onSeek={seek}
              onViewPrompt={setPromptTurn}
              canPlayUserClips={!!utterancesAsset}
              startTime={parsed.startTime}
            />
          </TabsContent>
          <TabsContent
            value="transcript"
            className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
          >
            <SyncedTranscript segments={parsed.speech} currentTime={currentTime} onSeek={seek} />
          </TabsContent>
          <TabsContent
            value="log"
            className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
          >
            <LogView
              text={log.data}
              isLoading={log.isLoading}
              hasLog={!!logAsset}
              query={searchQuery}
            />
          </TabsContent>
        </Tabs>
        <aside className="flex w-96 shrink-0 flex-col gap-3 overflow-auto">
          <section className="rounded-md border border-border p-3">
            <h3 className="mb-2 text-sm font-medium">Recording</h3>
            {recordingAsset ? (
              <audio
                ref={recordingRef}
                controls
                className="w-full"
                src={recording.url ?? undefined}
                onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
              >
                <track kind="captions" />
              </audio>
            ) : (
              <p className="text-xs text-muted-foreground">No recording for this call.</p>
            )}
            <audio ref={clipRef} className="hidden">
              <track kind="captions" />
            </audio>
            {wantClips && utterances.isLoading && (
              <p className="mt-2 text-xs text-muted-foreground">Loading utterance audio…</p>
            )}
          </section>
          <SidePanels
            context={parsed.context}
            agenda={parsed.agenda}
            coveredTopics={parsed.coveredTopics}
            usage={parsed.usage}
          />
        </aside>
      </div>
    );
  })();

  return (
    <>
      {breadcrumb}
      <div className="flex h-full min-h-0 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="mr-2 text-lg font-semibold">Call Console</h1>
          <span className="font-mono text-xs text-muted-foreground">{callId}</span>
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search events and log (regex)"
            className="ml-auto h-8 w-72"
            aria-label="Search console"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!richAsset}
            onClick={() => download(richAsset?.id, `transcript_${callId}.ndjson`)}
          >
            <Download className="mr-1 h-3 w-3" />
            Transcript
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!logAsset}
            onClick={() => download(logAsset?.id, `${callId}.log`)}
          >
            <Download className="mr-1 h-3 w-3" />
            Log
          </Button>
        </div>
        {body}
      </div>
      <PromptSheet turnId={promptTurn} prompt={prompt} onClose={() => setPromptTurn(null)} />
    </>
  );
};
